import { useState, useEffect, useRef } from 'react';
import { getNextPeriodCountdown } from '../utils/timeCalculations';
import { calculateDynamicOdds, calculateTimeBasedOdds, formatOdds, getOddsColorClass, getCountdownColorClass, formatCountdown } from '../utils/oddsFormulas';
import { useWebSocket } from '../../../../hooks/useWebSocket';

/**
 * 生成AI预测数据
 * 基于时间周期和随机波动模拟真实的市场预测
 */
function generateAIPrediction(periodMinutes, lastUpdate = 0) {
  // 基于时间周期的基础倾向
  const timeBias = {
    5: { bullish: 52, bearish: 48 },   // 短期稍微偏多
    15: { bullish: 48, bearish: 52 },  // 中期稍微偏空
    30: { bullish: 55, bearish: 45 }   // 长期偏多
  };
  
  const base = timeBias[periodMinutes] || { bullish: 50, bearish: 50 };
  
  // 添加随机波动 (±15%)
  const volatility = 15;
  const randomFactor = (Math.random() - 0.5) * volatility;
  
  let bullish = Math.max(20, Math.min(80, base.bullish + randomFactor));
  let bearish = 100 - bullish;
  
  // 确保数值为整数
  bullish = Math.round(bullish);
  bearish = Math.round(bearish);
  
  return {
    bullish,
    bearish,
    confidence: Math.random() * 0.3 + 0.7, // 0.7-1.0
    lastUpdate: Date.now()
  };
}

/**
 * 下注倒计时管理Hook
 * 管理多个时间周期的倒计时和赔率计算
 */
export const useBettingTimer = () => {
  // 定义时间周期常量
  const periods = [
    { minutes: 5, label: '5M' },
    { minutes: 15, label: '15M' },
    { minutes: 30, label: '30M' }
  ];

  const [countdowns, setCountdowns] = useState({});
  const [aiPredictions, setAiPredictions] = useState({});
  const aiPredictionsRef = useRef({});
  const lastAiUpdateRef = useRef(Date.now());
  
  // 使用WebSocket Hook
  const { lastUpdate, isConnected } = useWebSocket();

  // 同步 ref 和 state
  useEffect(() => {
    aiPredictionsRef.current = aiPredictions;
  }, [aiPredictions]);

  // 更新AI预测数据（WebSocket事件驱动）
  const updateAiPredictions = () => {
    const newPredictions = {};
    const periods = [
      { minutes: 5, label: '5M' },
      { minutes: 15, label: '15M' },
      { minutes: 30, label: '30M' }
    ];
    periods.forEach(period => {
      newPredictions[period.minutes] = generateAIPrediction(period.minutes, lastAiUpdateRef.current);
    });
    setAiPredictions(newPredictions);
    lastAiUpdateRef.current = Date.now();
    // console.log('🎯 AI预测已更新 (WebSocket驱动):', newPredictions);
  };

  // 监听WebSocket数据更新，触发AI预测更新
  useEffect(() => {
    if (lastUpdate && isConnected) {
      // console.log('📡 WebSocket数据更新，触发AI预测更新');
      updateAiPredictions();
    }
  }, [lastUpdate, isConnected]);
  
  // 更新所有周期的倒计时（只更新时间相关字段，保持赔率和预测数据稳定）
  const updateCountdowns = () => {
    setCountdowns(prevCountdowns => {
      const newCountdowns = { ...prevCountdowns };
      
      periods.forEach(period => {
        const countdown = getNextPeriodCountdown(period.minutes);
        const existingData = prevCountdowns[period.minutes];
        
        // 只更新时间相关的字段，保持赔率和AI预测数据不变
        if (existingData) {
          newCountdowns[period.minutes] = {
            ...existingData,
            ...countdown, // 更新倒计时相关字段
            formattedTime: formatCountdown(countdown.remainingMinutes, countdown.remainingSeconds),
            countdownColorClass: getCountdownColorClass(countdown.totalSeconds)
          };
        } else {
          // 如果是首次创建，计算完整数据
          const odds = calculateTimeBasedOdds(
            countdown.totalSeconds,
            period.minutes * 60,
            period.minutes
          );
          
          newCountdowns[period.minutes] = {
            ...countdown,
            label: period.label,
            formattedTime: formatCountdown(countdown.remainingMinutes, countdown.remainingSeconds),
            odds: odds,
            formattedOdds: formatOdds(odds),
            oddsColorClass: getOddsColorClass(countdown.totalSeconds, period.minutes * 60),
            countdownColorClass: getCountdownColorClass(countdown.totalSeconds),
            aiPrediction: aiPredictionsRef.current[period.minutes] || { bullish: 50, bearish: 50, confidence: 0.5 }
          };
        }
      });
      
      return newCountdowns;
    });
  };

  // Set up timer to update every second
  useEffect(() => {
    // 初始化AI预测数据
    const initialPredictions = {};
    const periods = [
      { minutes: 5, label: '5M' },
      { minutes: 15, label: '15M' },
      { minutes: 30, label: '30M' }
    ];
    periods.forEach(period => {
      initialPredictions[period.minutes] = generateAIPrediction(period.minutes);
    });
    setAiPredictions(initialPredictions);
    lastAiUpdateRef.current = Date.now();
    
    // Set up interval for countdown only (every second)
    const countdownInterval = setInterval(() => {
      updateCountdowns(); // 只更新倒计时，AI预测由WebSocket事件驱动
    }, 1000);
    
    // Cleanup interval
    return () => clearInterval(countdownInterval);
  }, []); // 空依赖数组，避免无限循环

  // Get countdown data for specified period
  const getCountdownData = (periodMinutes) => {
    return countdowns[periodMinutes] || {
      label: `${periodMinutes}M`,
      formattedTime: '--:--',
      formattedOdds: '-.--x',
      oddsColorClass: 'text-gray-400',
      countdownColorClass: 'text-gray-400',
      totalSeconds: 0,
      aiPrediction: { bullish: 50, bearish: 50, confidence: 0.5 }
    };
  };

  // Check if betting is allowed (more than 10 seconds remaining)
  const canPlaceBet = (periodMinutes) => {
    const data = countdowns[periodMinutes];
    return data && data.totalSeconds > 10;
  };

  return {
    periods,
    countdowns,
    getCountdownData,
    canPlaceBet,
    isLoading: Object.keys(countdowns).length === 0
  };
}
